import { Suspense, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { metricKeyAtom } from '@/atoms/metric';
import { selectedTeamNameAtom } from '@/atoms/team/info';
import { teamIdAtom } from '@/atoms/team/team';
import { METRIC_KEY, TEAM_ID } from '@/consts/searchKeys';
import { teamEventMarkersDescriptionsAtom, teamEventMarkersForMetricAtom } from '@/atoms/queries/team/teamEventData';
import { useSetTeamDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetTeamDataDebounceFilterParams';
import { useFormatVideoEvents } from '@/hooks/useFormatVideoEvents';
import {
  EntityVideoPlayerPanelHeader,
  VideoPlayerPanelBodyBase,
  VideoPlayerPanelHeaderBase,
} from '@/components/video/VideoPlayerPanel';
import { Card } from '@statsbomb/kitbag-components';
import { useVideoDataFromEventMarkers } from '@/hooks/useVideoDataFromEventMarkers';

const TeamVideoPlayerPanelHeaderWithData = () => (
  <EntityVideoPlayerPanelHeader entityNameAtom={selectedTeamNameAtom} entityIdParam={TEAM_ID} />
);

const TeamVideoPlayerPanelHeader = () => (
  <Suspense fallback={<VideoPlayerPanelHeaderBase isLoading />}>
    <TeamVideoPlayerPanelHeaderWithData />
  </Suspense>
);

const TeamVideoPlayerPanelBodyWithData = () => {
  useSetTeamDataDebouncedFilterParams();
  const [searchParams] = useSearchParams();
  const metricKeyParam = searchParams.get(METRIC_KEY);
  const teamIdParam = searchParams.get(TEAM_ID);
  const { videoIds, unavailableVideosCount } = useVideoDataFromEventMarkers(teamEventMarkersForMetricAtom);
  const videoEventMarkersDescriptions = useAtomValue(teamEventMarkersDescriptionsAtom);
  const videoEvents = useFormatVideoEvents(videoIds, videoEventMarkersDescriptions);

  const setMetricKey = useSetAtom(metricKeyAtom);
  const setPlayerId = useSetAtom(teamIdAtom);

  useEffect(() => {
    if (teamIdParam && metricKeyParam) {
      setPlayerId(Number(teamIdParam));
      setMetricKey(metricKeyParam);
    }
  }, [teamIdParam, metricKeyParam]);

  return (
    <VideoPlayerPanelBodyBase
      isLoading={!teamIdParam || !metricKeyParam}
      videoEvents={videoEvents}
      unavailableVideosCount={unavailableVideosCount}
    />
  );
};

const TeamVideoPlayerPanelBody = () => (
  <Suspense fallback={<VideoPlayerPanelBodyBase videoEvents={[]} isLoading />}>
    <TeamVideoPlayerPanelBodyWithData />
  </Suspense>
);

export const TeamDataLockerVideoPreview = () => (
  <Card>
    <TeamVideoPlayerPanelHeader />
    <TeamVideoPlayerPanelBody />
  </Card>
);
